import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AutoTradeOrderHistoryPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [expandedOrderIds, setExpandedOrderIds] = useState([]);
  const [showOnlyManualActionOrders, setShowOnlyManualActionOrders] = useState(false);

  const fetchOrderHistory = useCallback(async (syncFromZerodha = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedDate) {
        params.date = selectedDate;
      }
      if (syncFromZerodha) {
        params.sync_from_zerodha = true;
      }
      
      const response = await api.get('/api/v1/auto-trade/history', { params });
      
      if (response.data && response.data.success) {
        setOrders(response.data.orders || []);
        setStatistics(response.data.statistics);
        setExpandedOrderIds([]);
      } else {
        setError('Failed to fetch order history');
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || err.message || 'Failed to fetch order history';
      console.error('❌ Error fetching order history:', errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchOrderHistory();
  }, [fetchOrderHistory]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'ORDER_PLACED': 'bg-yellow-100 text-yellow-800',
      'FILLED': 'bg-blue-100 text-blue-800',
      'FILLED_NO_EXIT': 'bg-red-100 text-red-800',
      'TARGET_HIT': 'bg-green-100 text-green-800',
      'STOP_LOSS_HIT': 'bg-red-100 text-red-800',
      'MANUAL_CLOSED': 'bg-purple-100 text-purple-800',
      'FAILED': 'bg-gray-100 text-gray-800',
      'PENDING': 'bg-gray-100 text-gray-800'
    };
    
    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getProfitLossBadge = (amount) => {
    if (amount === null || amount === undefined) return null;
    const isProfit = amount > 0;
    const colorClass = isProfit ? 'text-green-600' : 'text-red-600';
    const symbol = isProfit ? '+' : '';
    
    return (
      <span className={`font-semibold ${colorClass}`}>
        {symbol}{formatCurrency(amount)}
      </span>
    );
  };

  const getProfitLossPointsBadge = (points) => {
    if (points === null || points === undefined) return null;
    const isProfit = points > 0;
    const colorClass = isProfit ? 'text-green-600' : 'text-red-600';
    const symbol = isProfit ? '+' : '';
    
    return (
      <span className={`text-sm ${colorClass}`}>
        ({symbol}{points.toFixed(2)} pts)
      </span>
    );
  };

  const hasAlertDetails = (order) => Boolean(order?.error_message || order?.status === 'FILLED_NO_EXIT');

  const toggleExpandedOrder = (orderId) => {
    setExpandedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const visibleOrders = showOnlyManualActionOrders
    ? orders.filter((order) => order.status === 'FILLED_NO_EXIT')
    : orders;
  const manualActionOrderCount = orders.filter((order) => order.status === 'FILLED_NO_EXIT').length;
  const visibleAlertDetailOrderIds = visibleOrders.filter(hasAlertDetails).map((order) => order.id);
  const allVisibleErrorRowsExpanded =
    visibleAlertDetailOrderIds.length > 0 &&
    visibleAlertDetailOrderIds.every((id) => expandedOrderIds.includes(id));

  const toggleExpandAllVisibleErrorRows = () => {
    if (allVisibleErrorRowsExpanded) {
      setExpandedOrderIds((prev) => prev.filter((id) => !visibleAlertDetailOrderIds.includes(id)));
      return;
    }
    setExpandedOrderIds((prev) => Array.from(new Set([...prev, ...visibleAlertDetailOrderIds])));
  };

  // Filter today's date for date input (max date)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-3xl font-bold text-gray-900">Automated NIFTY Trading Order History</h1>
                <div
                  className={`inline-flex items-center self-start rounded-full border px-3 py-1 text-sm font-semibold ${
                    manualActionOrderCount > 0
                      ? 'border-red-200 bg-red-50 text-red-800'
                      : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  Manual-action orders: {manualActionOrderCount}
                </div>
              </div>
              <p className="text-gray-600 mt-1">Complete history of all automated trades with profit/loss analysis</p>
            </div>

            {/* Statistics Cards */}
            {statistics && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Total Orders */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Orders</p>
                      <p className="text-2xl font-bold text-gray-900">{statistics.total_orders}</p>
                    </div>
                    <div className="text-3xl">📊</div>
                  </div>
                </div>

                {/* Overall P/L */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Overall P/L</p>
                      <p className={`text-2xl font-bold ${statistics.overall_profit_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(statistics.overall_profit_loss)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {statistics.overall_profit_loss_points >= 0 ? '+' : ''}{statistics.overall_profit_loss_points.toFixed(2)} points
                      </p>
                    </div>
                    <div className="text-3xl">💰</div>
                  </div>
                </div>

                {/* Profitable Orders */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Profitable Orders</p>
                      <p className="text-2xl font-bold text-green-600">{statistics.orders_profit}</p>
                      {statistics.total_orders > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {((statistics.orders_profit / statistics.total_orders) * 100).toFixed(1)}% win rate
                        </p>
                      )}
                    </div>
                    <div className="text-3xl">✅</div>
                  </div>
                </div>

                {/* Loss Orders */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Loss Orders</p>
                      <p className="text-2xl font-bold text-red-600">{statistics.orders_loss}</p>
                      {statistics.total_orders > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {((statistics.orders_loss / statistics.total_orders) * 100).toFixed(1)}% loss rate
                        </p>
                      )}
                    </div>
                    <div className="text-3xl">❌</div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Statistics */}
            {statistics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-sm text-gray-600">Targets Hit</p>
                  <p className="text-2xl font-bold text-green-600">{statistics.total_targets_hit}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-sm text-gray-600">Stop-Losses Hit</p>
                  <p className="text-2xl font-bold text-red-600">{statistics.total_stop_losses_hit}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-sm text-gray-600">Pending Orders</p>
                  <p className="text-2xl font-bold text-yellow-600">{statistics.orders_pending}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Date (Optional)
                  </label>
                  <input
                    type="date"
                    id="date-filter"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={today}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  {selectedDate && (
                    <button
                      onClick={() => {
                        setSelectedDate('');
                        fetchOrderHistory();
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                    >
                      Clear Filter
                    </button>
                  )}
                  <button
                    onClick={() => setShowOnlyManualActionOrders((prev) => !prev)}
                    className={`px-4 py-2 rounded-lg transition border ${
                      showOnlyManualActionOrders
                        ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                        : 'bg-white text-red-700 border-red-200 hover:bg-red-50'
                    }`}
                  >
                    {showOnlyManualActionOrders ? 'Showing manual-action only' : 'Show only manual-action orders'}
                  </button>
                  <button
                    onClick={() => fetchOrderHistory(true)}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
                    title="Sync P/L from Zerodha for orders that closed but missed postback"
                  >
                    🔗 Sync from Zerodha
                  </button>
                  <button
                    onClick={() => fetchOrderHistory()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Daily Profit/Loss Chart */}
            {statistics && statistics.daily_profit_loss && Object.keys(statistics.daily_profit_loss).length > 0 && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Profit/Loss</h3>
                <div className="space-y-2">
                  {Object.entries(statistics.daily_profit_loss)
                    .sort(([date1], [date2]) => new Date(date2) - new Date(date1))
                    .slice(0, 10)
                    .map(([date, amount]) => (
                      <div key={date} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <span className="font-medium text-gray-700">{date}</span>
                        <div className="text-right">
                          <span className={`font-semibold ${amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(amount)}
                          </span>
                          {statistics.daily_profit_loss_points[date] !== undefined && (
                            <span className={`text-sm ml-2 ${statistics.daily_profit_loss_points[date] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ({statistics.daily_profit_loss_points[date] >= 0 ? '+' : ''}{statistics.daily_profit_loss_points[date].toFixed(2)} pts)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Loading order history...</p>
              </div>
            )}

            {/* Orders Table */}
            {!loading && orders.length > 0 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Order History</h3>
                    {showOnlyManualActionOrders ? (
                      <p className="mt-1 text-sm text-red-600">
                        Showing only `FILLED_NO_EXIT` orders that need manual stop-loss/target action.
                      </p>
                    ) : null}
                    {visibleAlertDetailOrderIds.length > 0 ? (
                      <p className="mt-1 text-sm text-gray-500">
                        {visibleAlertDetailOrderIds.length} visible order{visibleAlertDetailOrderIds.length > 1 ? 's have' : ' has'} alert details available.
                      </p>
                    ) : null}
                  </div>
                  {visibleAlertDetailOrderIds.length > 0 ? (
                    <button
                      type="button"
                      onClick={toggleExpandAllVisibleErrorRows}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      {allVisibleErrorRowsExpanded ? 'Collapse all error rows' : 'Expand all error rows'}
                    </button>
                  ) : null}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instrument</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target/SL</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alerts</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P/L Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P/L Points</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visibleOrders.map((order) => {
                        const isExpanded = expandedOrderIds.includes(order.id);
                        const showDetails = hasAlertDetails(order);
                        return (
                          <React.Fragment key={order.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(order.order_placed_at || order.filled_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{order.instrument}</div>
                                <div className="text-xs text-gray-500">Qty: {order.quantity}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                ₹{order.entry_price.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {order.exit_price ? `₹${order.exit_price.toFixed(2)}` : '-'}
                                {order.exit_reason && (
                                  <div className="text-xs text-gray-500">{order.exit_reason.replace('_', ' ')}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div>T: ₹{order.target_price.toFixed(2)}</div>
                                <div>SL: ₹{order.stop_loss_price.toFixed(2)}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(order.status)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {showDetails ? (
                                  <div className="flex items-center gap-2">
                                    {order.status === 'FILLED_NO_EXIT' ? (
                                      <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700">
                                        Manual action
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700">
                                        Broker error
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => toggleExpandedOrder(order.id)}
                                      className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                                    >
                                      {isExpanded ? 'Hide details' : 'View details'}
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {getProfitLossBadge(order.profit_loss_amount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {getProfitLossPointsBadge(order.profit_loss_points)}
                              </td>
                            </tr>
                            {showDetails && isExpanded ? (
                              <tr className="bg-gray-50/70">
                                <td colSpan={9} className="px-6 py-4">
                                  <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                                    {order.status === 'FILLED_NO_EXIT' ? (
                                      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                                        <div className="text-xs font-bold uppercase tracking-wide text-red-800">
                                          Manual SL/Target required
                                        </div>
                                        <div className="mt-1 text-sm text-red-900 leading-relaxed">
                                          Entry was placed, but SL/Target GTT could not be attached. Place stop-loss and target manually immediately.
                                        </div>
                                      </div>
                                    ) : null}
                                    {order.error_message ? (
                                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                        <div className="text-xs font-bold uppercase tracking-wide text-amber-800">
                                          Raw broker error
                                        </div>
                                        <div className="mt-1 break-words text-sm text-amber-900 leading-relaxed">
                                          {order.error_message}
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && visibleOrders.length === 0 && !error && (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders Found</h3>
                <p className="text-gray-600 mb-6">
                  {showOnlyManualActionOrders
                    ? 'No manual-action auto-trade orders found for the current filters.'
                    : selectedDate 
                      ? `No automated trading orders found for ${selectedDate}.`
                      : 'You haven\'t placed any automated trading orders yet.'}
                </p>
                <button
                  onClick={() => navigate('/nifty-signal')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Go to NIFTY Signal Analyzer
                </button>
              </div>
            )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AutoTradeOrderHistoryPage;
